<script setup lang="ts">
const { isDark } = useThemeMode();

interface ApplicationCard {
  number: string;
  title: string;
  description: string;
}

const emit = defineEmits<{ 'cta-clicked': []; }>();

const props = withDefaults(defineProps<{
  singleLineHeading?: string;
  heading?: string;
  headingPartTwo?: string;
  subtitle?: string;
  cards?: ApplicationCard[];
  ctaText?: string;
  ctaLink?: string | null;
  showCornerSquares?: boolean;
}>(), {
  singleLineHeading: '',
  heading: 'Real-World Applications',
  headingPartTwo: 'of AI-Enhanced Creativity',
  subtitle: 'AI is reshaping how brands create, innovate, and deliver standout visuals across every platform and industry.',
  cards: () => [],
  ctaText: 'Discover Industry Solutions',
  ctaLink: null,
  showCornerSquares: true,
});
</script>

<template>
  <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" :aria-label="heading">
    <!-- Section heading -->
    <div class="text-center mb-10">
      <h2
        class="text-[28px] lg:text-[48px] font-semibold leading-tight font-avant"
        style="background: linear-gradient(92deg, #12DBFF -3.57%, #BA55FE 80.72%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent;"
      >
        <template v-if="singleLineHeading">
          {{ singleLineHeading }}
        </template>
        <template v-else>
          {{ heading }}
          <br class="hidden lg:block" />{{ headingPartTwo }}
        </template>
      </h2>
      <p
        class="mt-4 text-[16px] lg:text-[18px] font-normal tracking-[0.36px] max-w-2xl mx-auto font-avant"
        :class="!isDark ? 'text-black/70' : 'text-white/70'"
      >
        {{ subtitle }}
      </p>
    </div>

    <!-- Steps grid with corner squares and frame -->
    <div class="steps-wrapper mt-10 px-4 max-w-[1100px] mx-auto">
      <div class="steps-grid-wrapper">
        <div class="steps-grid">
          <div
            v-for="(app, index) in cards"
            :key="app.number || index"
            class="step-cell"
          >
            <!-- Card -->
            <div
              class="step-card p-6 flex flex-col items-start gap-4 relative font-avant"
              :class="isDark ? 'bg-[#1B022B]' : 'bg-white'"
            >
              <!-- Step number -->
              <span class="step-number font-avant-demi">
                {{ index + 1 }}
              </span>

              <h3
                class="font-semibold text-[18px] min-h-8 lg:min-h-14 leading-normal"
                :class="isDark ? 'text-[#957AFF]' : 'text-[#613FAA]'"
              >
                {{ app.title }}
              </h3>
              <p
                class="text-[15px] leading-5"
                :class="isDark ? 'text-white/85' : 'text-black'"
              >
                {{ app.description }}
              </p>
            </div>
          </div>
        </div>

        <!-- Corner squares (desktop only) -->
        <template v-if="showCornerSquares">
          <span class="hidden lg:block corner-sq corner-tr" :class="isDark ? 'bg-[#957AFF]' : 'bg-[#4902A6]'"></span>
          <span class="hidden lg:block corner-sq corner-bl" :class="isDark ? 'bg-[#957AFF]' : 'bg-[#4902A6]'"></span>
          <span class="hidden lg:block corner-sq corner-br" :class="isDark ? 'bg-[#957AFF]' : 'bg-[#4902A6]'"></span>
          <span class="hidden lg:block corner-sq bottom-1" :class="isDark ? 'bg-[#957AFF]' : 'bg-[#4902A6]'"></span>
          <span class="hidden lg:block corner-sq bottom-2" :class="isDark ? 'bg-[#957AFF]' : 'bg-[#4902A6]'"></span>
          <span class="hidden lg:block corner-sq bottom-3" :class="isDark ? 'bg-[#957AFF]' : 'bg-[#4902A6]'"></span>
        </template>
      </div>
    </div>

    <!-- CTA -->
    <div class="flex justify-center mt-10">
      <WrButton :link="ctaLink || undefined" @click="!ctaLink ? emit('cta-clicked') : undefined" container-class="h-[48px]">{{ ctaText }}</WrButton>
    </div>
  </section>
</template>

<style scoped>
.steps-wrapper {
  position: relative;
}

.steps-grid-wrapper {
  position: relative;
}

/* Grid layout */
.steps-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 640px) {
  .steps-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .steps-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    align-items: stretch;
  }
}

/* Card styling — border on each card, equal height */
.step-card {
  position: relative;
  border: 1px solid rgba(165, 80, 255, 0.25);
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Top decorative bar — gradient angled strip */
.step-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(
    92deg,
    #12DBFF 0%,
    #BA55FE 100%
  );
  clip-path: polygon(0 0, 45% 0, 35% 100%, 0 100%);
}

/* Vertical dividers between cards — right side of each cell except last */
.step-cell {
  position: relative;
  display: flex;
}

@media (min-width: 1024px) {
  .step-cell:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 0;
    right: -1px;
    width: 1px;
    height: 100%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(165, 80, 255, 0.4) 20%,
      rgba(165, 80, 255, 0.4) 80%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 2;
  }
}

/* Step number */
.step-number {
  width: 39px;
  height: 56px;
  font-size: 69.333px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 20px;
}

.step-number:where(.dark *) {
  color: #957AFF;
}

.step-number:where(:not(.dark *)) {
  color: #613FAA;
}

/* Corner squares */
.corner-sq {
  position: absolute;
  width: 5.62px;
  height: 5.577px;
  z-index: 10;
}

.corner-tr {
  top: -2.79px;
  right: -2.81px;
}

.corner-bl {
  bottom: -2.79px;
  left: -2.81px;
}

.corner-br {
  bottom: -2.79px;
  right: -2.81px;
}

/* Bottom intersection squares — between cards */
.bottom-1 {
  bottom: -2.79px;
  left: 25%;
  transform: translateX(-50%);
}

.bottom-2 {
  bottom: -2.79px;
  left: 50%;
  transform: translateX(-50%);
}

.bottom-3 {
  bottom: -2.79px;
  left: 75%;
  transform: translateX(-50%);
}
</style>
